import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Container,
  TextField,
  InputAdornment,
  Badge,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import StoreIcon from '@mui/icons-material/Store';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const announcements = [
  '✨ Free Shipping on Orders Above ₹5,000 ✨',
  '🎉 New Silver Collection Launched — Shop Now! 🎉',
  '💎 BIS Hallmarked Silver | 100% Purity Guaranteed 💎',
  '🏪 Visit Us: Vengamedu, Karur | Paramathi Velur | Namakkal 🏪',
];

const categoryNav = [
  { label: 'Necklaces', path: '/store/necklaces' },
  { label: 'Earrings', path: '/store/earrings' },
  { label: 'Rings', path: '/store/rings' },
  { label: 'Bangles', path: '/store/bangles' },
  { label: 'Chains', path: '/store/chains' },
  { label: 'Pendants', path: '/store/pendants' },
  { label: 'Coins & Bars', path: '/store/coins-bars' },
  { label: 'Pooja Items', path: '/store/pooja-items' },
];

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setInterval(() => {
      setAnnouncementIndex((prev) => (prev + 1) % announcements.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const menuItems = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'Store', path: '/store', icon: <StoreIcon /> },
    // { label: 'Collections', path: '/store', icon: <DiamondIcon /> },
    { label: 'Contact Us', path: '/contact', icon: <ContactMailIcon /> },
  ];

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <>
      {/* Announcement Bar */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #0F0D3D 0%, #1B1464 50%, #0F0D3D 100%)',
          color: '#D4AF37',
          py: 0.8,
          textAlign: 'center',
          fontSize: '0.8rem',
          fontWeight: 500,
          letterSpacing: '0.5px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            fontWeight: 500,
            letterSpacing: '0.5px',
            animation: 'fadeInOut 4s ease-in-out',
          }}
          key={announcementIndex}
        >
          {announcements[announcementIndex]}
        </Typography>
      </Box>

      {/* Main Navbar */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              py: { xs: 0.5, md: 1 },
              gap: 2,
            }}
          >
            {/* Left: Mobile menu + Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, minWidth: { md: '280px' } }}>
              {isMobile && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{ mr: 0 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box
                component={Link}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  component="img"
                  src="/logo.jpeg"
                  alt="Valli Velli Maligai Logo"
                  sx={{
                    height: { xs: 38, md: 52 },
                    width: 'auto',
                    borderRadius: '50%',
                    border: '2px solid #D4AF37',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/')}
                />
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      color: '#1B1464',
                      lineHeight: 1.1,
                      fontSize: { sm: '0.95rem', md: '1.15rem' },
                    }}
                  >
                    VALLI VELLI
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: '#D4AF37',
                      fontWeight: 600,
                      letterSpacing: '2px',
                      fontSize: { sm: '0.6rem', md: '0.7rem' },
                    }}
                  >
                    MALIGAI
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Center: Search Bar (desktop) */}
            {!isMobile && (
              <Box sx={{ flex: 1, maxWidth: 500, mx: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search for silver jewelry, coins, articles..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '25px',
                      backgroundColor: '#F8F7F5',
                      '& fieldset': { borderColor: '#E8E6E3' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#1B1464' },
                      fontSize: '0.85rem',
                    },
                  }}
                />
              </Box>
            )}

            {/* Right: Nav links (desktop) + Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, minWidth: { md: '280px' }, justifyContent: 'flex-end' }}>
              {!isMobile && (
                <>
                  {menuItems.map((item) => (
                    <Button
                      key={item.label}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path ? '#1B1464' : '#555',
                        fontWeight: location.pathname === item.path ? 700 : 500,
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        px: 1.5,
                        borderBottom: location.pathname === item.path ? '2px solid #D4AF37' : '2px solid transparent',
                        borderRadius: 0,
                        '&:hover': {
                          color: '#1B1464',
                          borderBottom: '2px solid #D4AF37',
                          backgroundColor: 'transparent',
                        },
                        transition: 'all 0.3s',
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </>
              )}

              {isMobile && (
                <IconButton onClick={() => setSearchOpen(!searchOpen)} sx={{ color: '#1B1464' }}>
                  <SearchIcon />
                </IconButton>
              )}

              <IconButton sx={{ color: '#1B1464' }}>
                <PersonOutlineIcon />
              </IconButton>
              <IconButton sx={{ color: '#1B1464' }}>
                <Badge badgeContent={0} color="error" variant="dot" invisible>
                  <FavoriteBorderIcon />
                </Badge>
              </IconButton>
              <IconButton sx={{ color: '#1B1464' }}>
                <Badge badgeContent={0} color="error" variant="dot" invisible>
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Box>
          </Toolbar>

          {/* Mobile Search Bar */}
          {isMobile && searchOpen && (
            <Box sx={{ pb: 1.5, px: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search silver jewelry..."
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchOpen(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '25px',
                    backgroundColor: '#F8F7F5',
                    fontSize: '0.85rem',
                  },
                }}
              />
            </Box>
          )}
        </Container>

        {/* Category Navigation Bar (Desktop) */}
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: '#FAFAF8',
              borderTop: '1px solid #F0EDE8',
              py: 0.5,
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 0.5,
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {categoryNav.map((cat) => (
                  <Button
                    key={cat.label}
                    size="small"
                    onClick={() => navigate(cat.path)}
                    sx={{
                      color: '#666',
                      fontSize: '0.78rem',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#D4AF37',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {cat.label}
                  </Button>
                ))}
              </Box>
            </Container>
          </Box>
        )}
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { width: 300, backgroundColor: '#FAFAF8' },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src="/logo.jpeg"
                alt="Logo"
                sx={{ height: 40, borderRadius: '50%', border: '2px solid #D4AF37' }}
              />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1B1464', lineHeight: 1.1 }}>
                  VALLI VELLI
                </Typography>
                <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600, letterSpacing: 1 }}>
                  MALIGAI
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={toggleDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 1 }} />

          <List>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.label}
                component={Link}
                to={item.path}
                onClick={toggleDrawer(false)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  backgroundColor: location.pathname === item.path ? '#EDE9FE' : 'transparent',
                  '&:hover': { backgroundColor: '#F0EDE8' },
                }}
              >
                <ListItemIcon sx={{ color: '#1B1464', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    color: '#1A1A2E',
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 1 }} />

          <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, letterSpacing: 1, px: 2, display: 'block', mb: 1 }}>
            CATEGORIES
          </Typography>
          <List dense>
            {categoryNav.map((cat) => (
              <ListItem
                button
                key={cat.label}
                onClick={() => { navigate(cat.path); setDrawerOpen(false); }}
                sx={{ borderRadius: 1, py: 0.8, '&:hover': { backgroundColor: '#F0EDE8' } }}
              >
                <ListItemText
                  primary={cat.label}
                  primaryTypographyProps={{ fontSize: '0.85rem', color: '#555' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* CSS animation for announcement bar */}
      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
      `}</style>
    </>
  );
};

export default Navbar;
